import React from "react";
import { RecBar, RecIcon } from "../materials/MaterialRecordParts";
import { BarMenu } from "./TimeLogBarMenu";
import { TimeLogMaterialsSection } from "../TimeLogMaterialsSection";
import { TimeLogTasksSection } from "./TimeLogTasksSection";
import { formatCurrency } from "../../helpers/estimateTotals";
import {
  formatLogDay,
  formatLogDuration,
  formatLogRange,
  formatLogTimestamp,
  payrollPill,
  titleCase,
} from "./timeLogModel";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { DeskCol, DeskGrid } from "../ops/DeskCols";
import "../ops/opsDesktop.css";
import "../materials/materialRecord.css";
import "./timeLogs.css";

function InfoRow({ icon, label, children }) {
  return (
    <div className="tl-info">
      <span className="tl-info__icon">
        <RecIcon name={icon} />
      </span>
      <span className="tl-info__body">
        <span className="tl-info__label">{label}</span>
        <span className="tl-info__value">{children}</span>
      </span>
    </div>
  );
}

/**
 * VIEW MODE for one Time Log. Shows what the record already holds; the rate
 * and payroll lines appear exactly where the previous form showed them.
 */
export function TimeLogDetails({
  log,
  canEdit = true,
  canArchive = false,
  canDelete = false,
  busy = false,
  onBack,
  onEdit,
  onArchive,
  onDelete,
}) {
  const isDesktop = useIsDesktopViewport();
  const pill = payrollPill(log);
  const range = formatLogRange(log);
  const workType = String(log.work_category || "").trim();
  const workDone = String(log.work_completed || "").trim();
  const notes = String(log.notes || "").trim();
  const breakMinutes = Math.floor((Number(log.break_seconds) || 0) / 60);
  const labour = [log.trade_name, log.classification_name].filter(Boolean).join(" · ");
  const menu = [
    ...(canArchive
      ? [{ key: "archive", label: "Archive time log", danger: true, onSelect: onArchive }]
      : []),
  ];

  const notesAndMaterials = (
    <>
        {notes ? (
          <section className="rec-card rec-desc">
            <div className="rec-label">
              <RecIcon name="doc" />
              Notes
            </div>
            <p className="rec-desc__text">{notes}</p>
          </section>
        ) : null}

        {log.id ? (
          <section className="rec-card tl-materials">
            <TimeLogMaterialsSection timeLog={log} readOnly />
          </section>
        ) : null}
    </>
  );

  return (
    <div className={`screen nx-screen rec-page${isDesktop ? " rec-page--detail tl-desk" : ""}`} data-testid="time-log-details">
      <RecBar title="Time Log Details" onBack={onBack} trailing={<BarMenu items={menu} />} />

      <div className="rec-body">
        <DeskGrid on={isDesktop} className="tl-desk-grid">
        <DeskCol on={isDesktop}>
        <section className="rec-card tl-hero">
          <div className="tl-hero__when">
            <div className="tl-hero__date">{formatLogDay(log.date_iso)}</div>
            {range ? <div className="tl-hero__range">{range}</div> : null}
          </div>
          <div className="tl-hero__right">
            <span className={`rec-pill tl-pill tl-pill--${pill.tone}`}>{pill.label}</span>
            <div className="tl-hero__dur" data-testid="time-log-duration">
              {formatLogDuration(log)}
            </div>
          </div>
        </section>

        <section className="rec-card tl-infolist" aria-label="Details">
          <InfoRow icon="folder" label="Project">
            <b>{log.project_name || "No Project"}</b>
            {log.client_name ? <small>{log.client_name}</small> : null}
          </InfoRow>
          <InfoRow icon="user" label="Employee">
            {log.employee || "—"}
          </InfoRow>
          {workType || workDone ? (
            <InfoRow icon="wrench" label="Work Type">
              {workType ? <b>{workType}</b> : null}
              {workDone ? <small>{workDone}</small> : null}
            </InfoRow>
          ) : null}
          {breakMinutes > 0 ? (
            <InfoRow icon="clock" label="Break">
              {breakMinutes} min
            </InfoRow>
          ) : null}
          {labour ? (
            <InfoRow icon="user" label="Trade / Classification">
              {labour}
            </InfoRow>
          ) : null}
        </section>

        {log.id ? <TimeLogTasksSection log={log} /> : null}

        {isDesktop ? notesAndMaterials : null}
        </DeskCol>
        <DeskCol on={isDesktop}>
        <section className="rec-card tl-infolist" aria-label="Payroll">
          <InfoRow icon="dollar" label="Payroll Type">
            {log.payroll_type ? titleCase(log.payroll_type) : "—"}
          </InfoRow>
          <InfoRow icon="tag" label="Hourly Rate">
            {formatCurrency(Number(log.hourly_rate) || 0)} / hr
          </InfoRow>
          <InfoRow icon="check" label="Status">
            {titleCase(log.approval_status || "pending")} · {pill.label}
          </InfoRow>
        </section>

        {isDesktop ? null : notesAndMaterials}

        {log.created_at || log.updated_at ? (
          <div className="tl-meta">
            {log.created_at ? <div>Created {formatLogTimestamp(log.created_at)}</div> : null}
            {log.updated_at ? <div>Updated {formatLogTimestamp(log.updated_at)}</div> : null}
          </div>
        ) : null}
        </DeskCol>
        </DeskGrid>
      </div>

      <div className="rec-actions">
        {canDelete ? (
          <button
            type="button"
            className="rec-btn rec-btn--danger"
            onClick={onDelete}
            disabled={busy}
          >
            <RecIcon name="trash" />
            Delete
          </button>
        ) : null}
        {canEdit ? (
          <button
            type="button"
            className="rec-btn rec-btn--outline"
            onClick={onEdit}
            disabled={busy}
          >
            <RecIcon name="pencil" />
            Edit
          </button>
        ) : null}
      </div>
    </div>
  );
}
